import { useCallback, useEffect, useMemo } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { addDays } from 'date-fns'
import { useUnit } from 'effector-react'
import type { FieldArrayWithId, FieldErrors } from 'react-hook-form'
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
  useWatch,
} from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import BackIcon from '@/assets/icons/icon-back.svg'
import { Button } from '@/components/ui/button.tsx'
import { EmptyProvider } from '@/components/ui/empty/empty-provider.tsx'
import { EmptyTableContent } from '@/components/ui/empty/empty-table-content.tsx'
import { FormCheckbox } from '@/components/ui/form/form-checkbox.tsx'
import { FormMultiFieldGroup } from '@/components/ui/form/form-multi-field-group'
import { FormNumberField } from '@/components/ui/form/form-number-field.tsx'
import { FormSection } from '@/components/ui/form/form-section.tsx'
import { Switch } from '@/components/ui/switch'
import { useToast } from '@/components/ui/toast.tsx'
import { $currentCreator } from '@/models/creators.ts'
import { TimeInputSection } from '@/modules/expiring-fans/components/time-input-section.tsx'
import { FollowUpsMessages } from '@/modules/message/components/follow-ups/follow-ups-messages.tsx'
import { MessageSequenceEditor } from '@/modules/message/components/sequence/message-sequence-editor'
import { MessageContextType } from '@/modules/message/components/sequence/types.ts'
import {
  $followUpMessages,
  $sendAfterMinutes,
  $sendIfNotSeenParentMessage,
  resetFollowUpState,
  resetSendAfterMinutesError,
  validateSendAfterMinutes,
} from '@/modules/message/models.ts'
import { AppRoutes } from '@/utils/constants.ts'
import { useCreatorUrl } from '@/utils/creators.ts'
import { handleError } from '@/utils/error'
import { classNames } from '@/utils/helpers'

import { useMassMessageMutations } from '../hooks/use-mass-message-mutations'
import type {
  BulkMessage,
  BulkMessageResponseContent,
  MassMessageCreateRequest,
  MassMessagesFormValues,
  MessageSequence,
} from '../types'
import { MassMessagesCreateFormSchema } from '../types'
import { ListsSelector } from '../ui/list-selector'
import { CalendarFormInput } from '../ui/time-calendar/calendar-input'
import { transformListsToSelectObject } from '../utils'

interface MassMessageFormProps {
  data?: Omit<Partial<BulkMessageResponseContent>, 'message'> & {
    message?: Partial<BulkMessage>
  }
  mode?: 'copy' | 'edit' | 'info'
}

type MessageFieldType = FieldArrayWithId<MessageSequence, 'messages', 'id'>

export const components = [
  'text',
  'media',
  'price',
  'fallback',
  'customAndFanNameLabels',
] as const

export const MassMessageForm = ({ data, mode }: MassMessageFormProps) => {
  const activeCreator = useUnit($currentCreator)
  const redirect = useNavigate()

  const toast = useToast()

  const { createMutation, updateMutation } = useMassMessageMutations()
  const followUpMessages = useUnit($followUpMessages)
  const sendAfterMinutes = useUnit($sendAfterMinutes)
  const sendIfNotSeenParentMessage = useUnit($sendIfNotSeenParentMessage)

  const isInfoMode = mode === 'info'

  const defaultValues: MassMessagesFormValues = useMemo(() => {
    const autoWithdrawAfterMinutes = Number(
      data?.message?.autoWithdrawAfterMinutes ?? 0,
    )
    const autoWithdrawHours = Math.floor(autoWithdrawAfterMinutes / 60)
    const autoWithdrawMinutes = autoWithdrawAfterMinutes % 60

    return {
      cooldownDuration: data?.message?.cooldownDuration ?? 60,
      excludeList: data?.message?.excludeList || [],
      minuteDelay: data?.message?.minuteDelay || 0,
      autoWithdrawHours: autoWithdrawHours ?? '',
      autoWithdrawMinutes: autoWithdrawMinutes ?? '',
      autoWithdrawAfterMinutes: data?.message?.autoWithdrawAfterMinutes ?? 0,
      autoWithdrawIfNotReplied:
        data?.message?.autoWithdrawIfNotReplied ?? false,
      expiresAt: data?.message?.expiresAt
        ? new Date(data?.message?.expiresAt)
        : addDays(new Date(), 1),
      includeList: data?.message?.includeList || [],
      messages: data?.message?.currentSequence?.messages || [],
      onlyActiveFans: data?.message?.onlyActiveFans || false,
      sendAsFirstMessage: data?.message?.sendAsFirstMessage || false,
      startsAt: data?.message?.startsAt
        ? new Date(data?.message?.startsAt)
        : new Date(),
      autoWithdrawActivated: data?.message?.autoWithdrawActivated || false,
      followUpEnabled: data?.message?.followUpEnabled || false,
    }
  }, [data])

  const listOptions = transformListsToSelectObject(activeCreator?.lists)

  const form = useForm<MassMessagesFormValues>({
    defaultValues,
    resolver: zodResolver(MassMessagesCreateFormSchema),
  })

  useEffect(() => {
    return () => resetFollowUpState()
  }, [])

  const {
    fields,
    update: handleUpdateField,
    append: handleAddField,
    remove: handleRemoveField,
  } = useFieldArray({
    control: form.control,
    keyName: 'uuid',
    name: 'messages',
  })

  const [excludedFanLists, includedFanLists, followUpEnabled] = useWatch({
    name: ['excludeList', 'includeList', 'followUpEnabled'],
    control: form.control,
  })

  // Todo: bug hotix
  for (let i = 0; i < fields.length; i++) {
    fields[i].id = i
  }

  const { handleSubmit, getValues } = form
  const parentUrl = useCreatorUrl(AppRoutes.Creator.Settings.OnlineMassMessages)
  const handleRedirect = () => {
    resetFollowUpState()
    redirect(parentUrl)
  }

  const handleAddMessage = useCallback(
    async (message: MessageFieldType) => {
      handleAddField({
        ...message,
        id: fields.length === 0 ? 0 : fields.length,
      })
    },
    [fields, handleAddField],
  )

  const handleRemoveMessage = useCallback(
    async ({ id }: { id: number }) => {
      handleRemoveField(id)
    },
    [handleRemoveField],
  )

  const handleUpdateMessage = useCallback(
    async (message: MessageFieldType) => {
      handleUpdateField(message.id, message)
    },
    [handleUpdateField],
  )

  const handleFormError = useCallback(
    (errors: FieldErrors<MassMessagesFormValues>) => {
      const description = Object.values(errors)
        .map((error) => error?.message)
        .join(',\n')

      toast.error('Please fix the errors in the form', { description })
    },
    [toast],
  )
  const autoWithdrawActivated = form.watch('autoWithdrawActivated')

  const validateForm = () => {
    validateSendAfterMinutes()
    toast.error('Please fill required fields')
  }

  const handleFormSubmit = useCallback(
    async ({
      autoWithdrawHours = 0,
      autoWithdrawMinutes = 0,
      autoWithdrawAfterMinutes,
      ...values
    }: MassMessagesFormValues) => {
      if (followUpEnabled && !sendAfterMinutes) {
        validateForm()

        return
      }

      const requestBody: MassMessageCreateRequest = {
        ...values,
        minuteDelay: values?.minuteDelay || 0,
        followUpDto: {
          messages: followUpMessages,
          sendAfterMinutes,
          sendIfNotSeenParentMessage,
        },
      }

      if (autoWithdrawActivated && (autoWithdrawHours || autoWithdrawMinutes)) {
        const totalMinutes =
          Number(autoWithdrawHours) * 60 + Number(autoWithdrawMinutes)

        requestBody.autoWithdrawAfterMinutes = totalMinutes
      } else {
        requestBody.autoWithdrawAfterMinutes = autoWithdrawAfterMinutes
      }

      try {
        if (mode === 'edit') {
          await updateMutation
            .mutateAsync({
              messageId: data?.message?.id,
              values: requestBody,
            })
            .then(() => {
              toast.success('Successfully updated message')
            })
        } else {
          await createMutation.mutateAsync(requestBody).then(() => {
            toast.success('Successfully created message')
          })
        }

        handleRedirect()
      } catch (error) {
        handleError(error, 'Something went wrong while creating the message')
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      autoWithdrawActivated,
      createMutation,
      data?.message?.id,
      handleRedirect,
      mode,
      toast,
      updateMutation,
      validateForm,
    ],
  )

  const handleToggleChange = useCallback(
    (onChange: (value: boolean) => void) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        onChange(event.target.checked)
        resetSendAfterMinutesError()
      },
    [],
  )

  const renderEmpty = useCallback(
    () => (
      <EmptyTableContent>
        It looks like you don&apos;t have any messages created yet, click on the{' '}
        <span className="text-pointer text-primary-500">Add Message</span>{' '}
        button to do so.
      </EmptyTableContent>
    ),
    [],
  )

  const disableSave =
    isInfoMode ||
    fields.length === 0 ||
    (followUpEnabled && !followUpMessages.length)

  const handleMinuteDelayBlur = useCallback(
    (value: number | null, onChange: (value: number | null) => void) => {
      if (value !== null && (value.toString() === '' || value < 0)) {
        onChange(0)
      }
    },
    [],
  )

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(handleFormSubmit, handleFormError)}>
        <div
          onClick={handleRedirect}
          className="text-primary-500 flex cursor-pointer items-center gap-2 text-sm font-medium"
        >
          <BackIcon />
          <span>Back to Online Mass Messages</span>
        </div>

        <header className="mt-4 flex flex-col">
          <h1 className="text-title text-xl font-semibold">{getTitle(mode)}</h1>
          <h2 className="text-text-sub mt-1 text-sm font-normal">
            Configure your Online Mass Message to be sent out to users who are
            online during the selected time frame.
          </h2>
        </header>

        <div className="bg-card mt-6 rounded-lg px-6">
          <div className="flex flex-col gap-10 border-b py-6 md:flex-row">
            <CalendarFormInput
              disabled={isInfoMode}
              name="startsAt"
              label="Start Date"
            />

            <CalendarFormInput
              name="expiresAt"
              label="End Date"
              fromDate={getValues('startsAt')}
              disabled={isInfoMode}
            />
          </div>

          <div className="flex flex-col gap-10 border-b py-6 md:flex-row">
            <ListsSelector
              label="Only include the selected Fan Lists"
              name="includeList"
              placeholder="Select Lists"
              lists={listOptions}
              disabled={isInfoMode}
              excludeValues={excludedFanLists}
            />

            <ListsSelector
              label="Exclude the selected Fan Lists"
              disabled={isInfoMode}
              placeholder="Select Lists"
              name="excludeList"
              lists={listOptions}
              excludeValues={includedFanLists}
            />
          </div>

          <FormSection>
            <FormNumberField
              name="cooldownDuration"
              label="Exclude Fans you have chatted with recently"
              prefix="Minutes"
              disabled={isInfoMode}
            />
          </FormSection>

          <div className="border-b">
            <Controller
              control={form.control}
              name="minuteDelay"
              render={({ field: { value, onChange } }) => (
                <TimeInputSection
                  title="Set Delay"
                  disabled={isInfoMode || updateMutation.isLoading}
                  description="The number of minutes when the message will be sent, after the fan got online"
                  unit="Minutes"
                  textClassName="max-w-[134px]"
                  value={typeof value === 'number' ? value : undefined}
                  onBlur={() => handleMinuteDelayBlur(value, onChange)}
                  onChange={onChange}
                />
              )}
            />
          </div>

          <FormSection>
            <div className="flex w-full flex-col">
              <div className="flex w-full items-start">
                <FormMultiFieldGroup
                  label="Auto Withdraw"
                  fields={[
                    <FormNumberField
                      name="autoWithdrawHours"
                      postfix="Hours"
                      disabled={isInfoMode || !autoWithdrawActivated}
                      key="hours"
                      inputClassName="w-10"
                      max={24}
                      containerClassName="border-r-0 rounded-r-none"
                    />,
                    <FormNumberField
                      name="autoWithdrawMinutes"
                      postfix="Minutes"
                      disabled={isInfoMode || !autoWithdrawActivated}
                      key="minutes"
                      inputClassName="w-10"
                      containerClassName="rounded-l-none"
                      max={59}
                    />,
                  ]}
                  error={[
                    form.formState.errors?.autoWithdrawHours?.message,
                    form.formState.errors?.autoWithdrawMinutes?.message,
                  ]
                    .filter(Boolean)
                    .join(',\n')}
                />
                <label className="ml-auto flex items-center gap-2">
                  <span className="text-title text-sm font-medium">
                    Activate
                  </span>
                  <Controller
                    name="autoWithdrawActivated"
                    render={({ field: { value, onChange } }) => (
                      <Switch
                        checked={value}
                        onCheckedChange={onChange}
                        className={classNames('ml-auto', {
                          'cursor-not-allowed': isInfoMode,
                        })}
                        disabled={isInfoMode}
                      />
                    )}
                  />
                </label>
              </div>
              <FormCheckbox
                name="autoWithdrawIfNotReplied"
                className="mt-4"
                disabled={isInfoMode}
                label="Also withdraw if the message has been seen but not replied to"
              />
            </div>
          </FormSection>

          <FormSection isLast>
            <div className="flex flex-col gap-4">
              <div className="flex flex-row">
                <FormCheckbox
                  name="sendAsFirstMessage"
                  label="Send to fans you have never chatted with before"
                  disabled={isInfoMode}
                />
              </div>
              <div className="flex flex-row">
                <FormCheckbox
                  name="onlyActiveFans"
                  label="Only send to fans with an active subscription"
                  disabled={isInfoMode}
                />
              </div>
            </div>
          </FormSection>
        </div>
      </form>

      <EmptyProvider render={renderEmpty}>
        <MessageSequenceEditor
          className="pt-[32px]"
          title="Messages"
          description="Configure multiple messages that will be sent to your fans"
          placeholder="Hey %nick%, how are you?"
          dialogTitle="Add a new Message"
          dialogDescription="Use the button to insert the fan's name, which will replace the label with their actual name. If the fan doesn't have a name, the label will be replaced with the fallback name."
          entries={fields as any} // Todo: just a hotfix
          components={components}
          onRemove={handleRemoveMessage}
          onUpdate={handleUpdateMessage}
          onCreate={handleAddMessage}
          disabled={isInfoMode}
          type={MessageContextType.MassMessage}
        />
      </EmptyProvider>

      <div className="pt-[32px]">
        <Controller
          name="followUpEnabled"
          render={({ field: { value, onChange } }) => (
            <FollowUpsMessages
              type="Online Mass Message"
              dialogTitle="Add Follow Up Online Mass Message"
              customDescription="Automatically sends a follow-up if a fan sees but doesn’t reply to your Online Mass Message - and hasn’t received any other message from you since."
              enabled={value}
              toggleChange={handleToggleChange(onChange)}
              data={data?.followUpDto}
              disabled={isInfoMode}
            />
          )}
        />
      </div>

      <footer className="mt-6 flex justify-end">
        <Button
          type="submit"
          variant="default"
          disabled={disableSave}
          onClick={handleSubmit(handleFormSubmit, handleFormError)}
          isLoading={createMutation.isLoading || updateMutation.isLoading}
        >
          {mode === 'edit' ? 'Update Mass Message' : 'Create Mass Message'}
        </Button>
      </footer>
    </FormProvider>
  )
}

const getTitle = (mode?: 'copy' | 'edit' | 'info') => {
  switch (mode) {
    case 'copy':
      return 'Duplicate Online Mass Message'
    case 'edit':
      return 'Edit Online Mass Message'
    case 'info':
      return 'Online Mass Message'
    default:
      return 'Create Online Mass Message'
  }
}
